import { ToastType } from '@components';
import { getDB } from '@database';
import Dexie from 'dexie';

import { createError } from './errorUtils';
import { handleToast } from './utils';

export const CACHE_DURATION = {
  ONE_DAY: 24 * 60 * 60 * 1000,
  ONE_HOUR: 60 * 60 * 1000,
  TWO_HOUR: 2 * 60 * 60 * 1000,
  THREE_HOUR: 3 * 60 * 60 * 1000,
  FIVE_MIN: 5 * 60 * 1000,
  ONE_MIN: 60 * 1000,
  THIRTY_MIN: 30 * 60 * 1000,
};

interface FetchWithCacheOptions<T> {
  dbStore: Dexie.Table<
    { key: string; cacheDate: number; cacheDuration: number; items: T },
    string
  >;
  cacheKey: string;
  apiCall: () => Promise<T>;
  duration: number;
  forceRefresh?: boolean;
}

export async function setSingletonCache<T>(
  table: Dexie.Table<
    T & { key: string; cacheDate: number; cacheDuration: number },
    string
  >,
  data: Partial<T>,
  duration: number,
  key: string = 'singleton',
): Promise<void> {
  try {
    await table.put({
      items: data,
      key,
      cacheDate: Date.now(),
      cacheDuration: duration,
    } as unknown as T & {
      key: string;
      cacheDate: number;
      cacheDuration: number;
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to cache data:', e);
  }
}

export async function getValidSingletonCacheOrClear<T>(
  table: Dexie.Table<
    T & { key: string; cacheDate: number; cacheDuration: number },
    string
  >,
  key: string,
): Promise<T | null> {
  const cached = (await table.get(key)) as T & {
    key: string;
    cacheDate: number;
    cacheDuration: number;
    items: T;
  };
  if (!Boolean(cached)) return null;

  const { cacheDate, cacheDuration, items } = cached;

  const isValid = Date.now() - cacheDate <= cacheDuration;

  if (isValid) {
    return items;
  }

  // ❌ Expired → clear it
  await table.delete(key);
  return null;
}

export const fetchWithCache = async <T>({
  dbStore,
  cacheKey,
  apiCall,
  duration,
  forceRefresh = false,
}: FetchWithCacheOptions<T>): Promise<T | undefined> => {
  try {
    const db = getDB();

    if (!db) {
      return;
    }

    if (!forceRefresh) {
      const cached = await getValidSingletonCacheOrClear(dbStore, cacheKey);

      if (cached) {
        return cached as T;
      }
    }

    const data: T = await apiCall();

    await setSingletonCache(
      dbStore as unknown as Dexie.Table<
        T & { key: string; cacheDate: number; cacheDuration: number },
        string
      >,
      data,
      duration,
      cacheKey,
    );

    return data;
  } catch (error) {
    const e = createError(error, `Failed to fetch for key: ${cacheKey}`);
    handleToast(ToastType.Error, e.message);
  }
};
